// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// CareRx clinical-lane and PA-feed calls added after the E-05 freeze of `api/client.ts`.

import { jsonRequest as req } from './http'
import type { ServerPage } from '../components/Pager'

// ── ASP #23: items waiting in each Clinical stage (the Review · Renewals · Hold tab labels) ──────
/** `conditional` / `denied` (CareRx wave-1 #2) feed the Clinical Review tiles; older API bundles omit them. */
export interface ClinicalCounts { review: number; renewals: number; hold: number; conditional?: number; denied?: number }
export const getClinicalCounts = () => req<ClinicalCounts>('GET', '/carerx/clinical/counts')

// ── ASP #22: the follow-up date on a therapy that is on hold (YYYY-MM-DD, or null to clear) ──────
export const setHoldFollowUp = (therapyId: string, followUpOn: string | null) =>
  req<{ therapy_id: string; follow_up_on: string | null }>('PUT', `/carerx/therapies/${therapyId}/hold-follow-up`, { follow_up_on: followUpOn })

// ── ASP #14: remove a prescription from "PA approved — not yet in CareRx", and bring it back ──────
export interface DismissedCandidate {
  rx_id: string
  rx_number: string | null
  patient_id: string
  patient_name: string | null
  drug_name: string | null
  dismissal_reason: string
  dismissed_by_name: string | null
  dismissed_at: string
  /** ASP #9: language name (English included); null when Pioneer has none. */
  preferred_language?: string | null
}
export const dismissCandidate = (rxId: string, reason: string) =>
  req<{ rx_id: string; dismissed: true }>('POST', `/carerx/enrollment/candidates/${rxId}/dismiss`, { reason })
export const restoreCandidate = (rxId: string) =>
  req<{ rx_id: string; dismissed: false }>('POST', `/carerx/enrollment/candidates/${rxId}/restore`)
export const listDismissedCandidates = (page: number, pageSize: number) =>
  req<ServerPage<DismissedCandidate>>('GET', `/carerx/enrollment/candidates/dismissed?page=${page}&page_size=${pageSize}`)

// ── ASP #13: the drug's own questions (drug record → Drug specific assessment), answered here ─────
export interface DrugAssessmentItem {
  id: string
  question_text: string
  question_type: string
  options: string[]
  expected_answer: string | null
  out_of_expected: string
  asked_when: string
  required: boolean
  tag: string
  assessment_answer: string | null
  outside_expected: boolean
  answered_by_name: string | null
  answered_at: string | null
}
export interface DrugAssessment { therapy_id: string; items: DrugAssessmentItem[]; required_unanswered: number; flagged: number }
export const getDrugAssessment = (therapyId: string) =>
  req<DrugAssessment>('GET', `/carerx/therapies/${therapyId}/drug-assessment`)
export const answerDrugAssessment = (therapyId: string, questionId: string, answer: string) =>
  req<{ id: string; assessment_answer: string; outside_expected: boolean }>('POST', `/carerx/therapies/${therapyId}/drug-assessment/${questionId}`, { answer })

// ── ASP #28: the Lab Tracker's typed next action (null clears it) ─────────────────────────────────
export const setLabNextAction = (therapyId: string, text: string | null) =>
  req<{ therapy_id: string; next_action_text: string | null; next_action_by: string | null }>(
    'PUT', `/carerx/lab-tracker/${therapyId}/next-action`, { text })

// ── ASP #24: put a therapy on hold by hand, from the Hold tab (the manual "Send to Screening & Hold") ─
export const placeManualHold = (therapyId: string, reason: string) =>
  req<{ ok: boolean; moved: boolean; source?: string }>('POST', `/carerx/clinical-review/${therapyId}/screen-decision`,
    { move_to_screening: true, manual: true, reason })

// ── ASP #32: (re)assign a clinical review to a pharmacist of this pharmacy ─────────────────────────
export interface ReviewAssignee { id: string; display_name: string }
/** The pharmacists a review or enrollment can go to — the server's rule (mig 439 `carerx.pharmacist`).
 *  `fallback` = nobody here holds that permission yet, so the list is every CareRx staff member. */
export const listReviewAssignees = () => req<{ items: ReviewAssignee[]; fallback?: boolean }>('GET', '/carerx/clinical-review/assignees')
/** Where an assignment is made from — the new pharmacist's notification names it (ASP #27/#28). */
export type AssignContext = 'clinical_review' | 'enrollment' | 'care_plan'
/** `keepStatus`: assigning from the enrollment queue names the pharmacist without starting the review. */
export const assignReviewTo = (therapyId: string, rphId: string, keepStatus = false, context?: AssignContext) =>
  req<{ ok: boolean; assigned_rph_id: string; rph_name: string | null; previous_rph_id: string | null }>(
    'PUT', `/carerx/clinical-review/${therapyId}/assign`, { rph_id: rphId, keep_status: keepStatus || undefined, context })
/** The therapy's pharmacist alone (no patient fields) — the care-plan panels' assignee control. */
export interface ReviewAssignment { assigned_rph_id: string | null; rph_name: string | null; assigned_at: string | null }
export const getReviewAssignment = (therapyId: string) =>
  req<ReviewAssignment>('GET', `/carerx/clinical-review/${therapyId}/assignment`)
